import type { Product, ProductVariant } from '@commerce/types/product'
export type SelectedOptions = Record<string, string | null>
import { Dispatch, SetStateAction } from 'react'

export function getProductVariant(product: Product, opts: SelectedOptions) {
  const variant = product.variants.find((variant) => {
    return Object.entries(opts).every(([key, value]) =>
      variant.options.find((option) => {
        if (
          option.__typename === 'MultipleChoiceOption' &&
          option.displayName.toLowerCase() === key.toLowerCase()
        ) {
          return option.values.find((v) => v.label.toLowerCase() === value)
        }
      })
    )
  })
  return variant
}

export function selectDefaultOptionFromProduct(
  product: Product,
  updater: Dispatch<SetStateAction<SelectedOptions>>
) {
  // Selects the default option
  product.variants[0]?.options?.forEach((v) => {
    updater((choices) => ({
      ...choices,
      [v.displayName.toLowerCase()]: v.values[0].label.toLowerCase(),
    }))
  })
}

/**
 * This function sets the correct option value according to the metal type present in the slug.
 *
 * @param product the product object
 * @param updater the option type updater
 * @param slug the product slug
 *
 */
export function selectMetalOptionFromProductSlug(
  product: Product,
  updater: Dispatch<SetStateAction<SelectedOptions>>,
  slug: string
) {
  const value = getMetalValueFromSlug(product, slug)

  updater((choices) => ({
    ...choices,
    metal: value,
  }))
}

/**
 *
 * Extracts 'metal' option value from product slug.
 */
export function getMetalValueFromSlug(product: Product, slug: string) {
  return slug.replace(`${product.slug}-`, '').replace(/-/g, ' ').toLowerCase()
}

export function hasValidMetalOptionSlug(product: Product, slug: string) {
  const validMetalOptions = getProductMetalOptions(product)
  const value = getMetalValueFromSlug(product, slug)
  return validMetalOptions.includes(value)
}

export function getProductMetalOptions(product: Product) {
  let metalOptions: string[] = []

  Object.entries(product.options).forEach(([key, value]) => {
    if (
      value.__typename === 'MultipleChoiceOption' &&
      value.displayName.toLowerCase() === 'metal'
    ) {
      value.values.map((v) => metalOptions.push(v.label.toLowerCase()))
    }
  })

  return metalOptions
}

export function productHasMetalOptions(product: Product) {
  return getProductMetalOptions(product).length > 0
}

/**
 *
 * @param slug product slug
 * @returns true if the slug includes a valid metal option
 */
export function slugContainsMetalOption(slug: string) {
  // TODO: fetch all metal option types from the Spree API to avoid hardcoding the option types.
  // Currently, the Spree Storefront API doesn't offer an endpoint to fetch option types and values.
  //
  // An alternative for this would be using the Spree Platform API.
  //
  // More info:
  // https://api.spreecommerce.org/docs/api-v2/0e192836641aa-platform-api
  // https://spree-sdk.upsidelab.io/modules/_spree_platform_api_v2_sdk.html
  const metalOptionTypeSlugs = [
    'white-&-yellow-gold-14k',
    'white-&-yellow-gold-18k',
    'white-gold-14k',
    'white-gold-18k',
    'yellow-gold-14k',
    'yellow-gold-18k',
    'rose-gold-14k',
    'rose-gold-18k',
    'platinum',
  ]

  return metalOptionTypeSlugs.some((opt) => slug.endsWith(opt))
}

export function getVariantMetalOptionValue(variant: ProductVariant) {
  const metalOptionValue = variant.options.find((opt) => {
    if (opt.displayName.toLowerCase() === 'metal') {
      return opt.values[0].label
    }
  })

  return metalOptionValue?.values[0].label
}

export function setProductName(
  product: Product,
  activeVariant: ProductVariant | undefined
) {
  if (!productHasMetalOptions(product) || !activeVariant) return product.name

  const metalOptionValue = getVariantMetalOptionValue(activeVariant)

  if (!metalOptionValue) return product.name

  return `${product.name}`
}
